import { atom } from 'jotai'
import { Case } from '../../models/Case'
import { SortCaseProperty } from '../../utils/sortCasesHelper'
import {
    getClosedCases,
    getSalaryData,
    getNextClosedCasesPage,
} from '../../api/salary'
import { ClosedCaseList } from '../../models/ClosedCaseList'

const initSort: SortCaseProperty = {
    property: 'Default',
    direction: 'Default',
}

const openCasesAtom = atom<Case[]>([])
const casesToConfirmAtom = atom<Case[]>([])
const closedCasesAtom = atom<Case[]>([])
const closedCasesListAtom = atom<ClosedCaseList>(new ClosedCaseList())
const sortedCasesAtom = atom<SortCaseProperty>(initSort)
const sortedOpenCasesAtom = atom<SortCaseProperty>(initSort)
const sortedClosedCasesAtom = atom<SortCaseProperty>(initSort)
const isFetchingCasesAtom = atom(false)
const isFetchingClosedCasesAtom = atom(false)
const isFetchingNextClosedCasesPageAtom = atom(false)
const closedCasesPageAtom = atom(1)
const hasTestCasesAtom = atom(false)

const fetchClosedCasesAtom = atom(null, async (get, set) => {
    set(isFetchingClosedCasesAtom, true)
    const sorted = get(sortedClosedCasesAtom)
    const response = await getClosedCases({
        sortCaseProperty: sorted,
    })

    const closedCasesResponse = ClosedCaseList.build(response)

    set(closedCasesListAtom, closedCasesResponse)
    set(closedCasesAtom, closedCasesResponse.cases)
    set(isFetchingClosedCasesAtom, false)
})

// Separate atom for fetching data
const fetchCasesAtom = atom(null, async (get, set) => {
    set(isFetchingCasesAtom, true)
    const sorted = get(sortedOpenCasesAtom)
    const response = await getSalaryData({
        sortCaseProperty: sorted,
    })
    const openCasesResponse =
        response.aabenSagList.sager?.map((caseData) => Case.build(caseData)) ??
        []

    const casesToConfirmResponse =
        response.sagTilBekraeftList.sager?.map((caseData) =>
            Case.build(caseData)
        ) ?? []

    set(openCasesAtom, openCasesResponse)
    set(casesToConfirmAtom, casesToConfirmResponse)
    set(isFetchingCasesAtom, false)
    set(hasTestCasesAtom, response?.harTestSager ?? false)
})

// Read-only atom that depends on openCasesAtom
const filteredCasesAtom = atom((get) => {
    return get(openCasesAtom)
})

export const fetchClosedCasesWithPageAtom = atom(
    null,
    async (get, set, page: number) => {
        set(isFetchingNextClosedCasesPageAtom, true)
        set(closedCasesPageAtom, page)
        try {
            const currentList = get(closedCasesListAtom)
            const response = await getNextClosedCasesPage(
                page,
                currentList.caseListId
            )
            const closedCasesResponse = ClosedCaseList.build(response)
            const cases = [
                ...get(closedCasesListAtom)?.cases,
                ...closedCasesResponse.cases,
            ]
            closedCasesResponse.cases = cases
            set(closedCasesListAtom, closedCasesResponse)
            set(closedCasesAtom, cases)
        } catch (error) {
            console.error('Error fetching closed cases:', error)
        } finally {
            set(isFetchingNextClosedCasesPageAtom, false)
        }
    }
)

export {
    openCasesAtom,
    closedCasesAtom,
    sortedCasesAtom,
    sortedOpenCasesAtom,
    sortedClosedCasesAtom,
    filteredCasesAtom,
    casesToConfirmAtom,
    fetchCasesAtom,
    fetchClosedCasesAtom,
    closedCasesListAtom,
    isFetchingCasesAtom,
    isFetchingClosedCasesAtom,
    closedCasesPageAtom,
    isFetchingNextClosedCasesPageAtom,
    hasTestCasesAtom,
}
